import React, { useMemo } from 'react'
import { ChartBar, ChartLine } from '../../../shared/chart/chart'
import { DashboardHeader, Widget } from '../../../shared/chart/widget'
import { IconAuthLogin, IconCancel, IconCheck, IconCourseLaunch, IconSession, IconUsers } from '../../../shared/icon/icon'
import {
  COLOR_COMPLETIONS,
  COLOR_LAUNCHES,
  COLOR_SCORES,
  COMPLETED_OUTCOMES,
  FAILED_OUTCOMES,
  SCORE_LABELS,
  bucketLabel,
  formatCount,
  formatScore
} from './launch-format'
import { ChartBox } from './chart-box'

// The top of the Launches tab: the range's totals, launches and completions
// over time, the six outcomes and the scores.

const TILES = [
  { key: 'launches', text: 'Launches', Icon: IconCourseLaunch, hint: 'accepted launches' },
  { key: 'uniqueLearners', text: 'Unique learners', Icon: IconUsers, hint: 'counted over the whole range' },
  { key: 'signedIn', text: 'Signed in', Icon: IconAuthLogin, hint: 'launches that signed in' },
  { key: 'started', text: 'Started', Icon: IconSession, hint: 'launches that began a session' },
  { key: 'completions', text: 'Completed', Icon: IconCheck, hint: 'sessions completed in the range' },
  { key: 'refusals', text: 'Refusals', Icon: IconCancel, hint: 'refused attempts' }
]

export const LaunchTiles = ({ totals }) =>
  <div className="row row-cols-2 row-cols-md-3 row-cols-xl-6 g-2 mb-3" data-testid="lms-launch-tiles">
    {TILES.map(tile =>
      <div className="col" key={tile.key}>
        <div className="card h-100" data-testid={`tile-${tile.key}`}>
          <div className="card-body p-2">
            <Widget
              header={<DashboardHeader Icon={tile.Icon} text={tile.text} value={formatCount(totals[tile.key])} />}
              footer={<small className="text-body-secondary">{tile.hint}</small>}
            />
          </div>
        </div>
      </div>
    )}
  </div>

const line = (buckets, label, key, color) => ({
  label,
  data: buckets.map(bucket => bucket[key]),
  borderColor: color,
  backgroundColor: color,
  pointBackgroundColor: color,
  pointBorderColor: color,
  pointRadius: 2,
  borderWidth: 2,
  tension: 0.2,
  fill: false
})

// Launches and completions per bucket. Both are kept rows (a launch is never
// deleted), so nothing here is greyed for retention.
export const LaunchesChart = ({ buckets }) => {
  const labels = useMemo(() => buckets.map(bucket => bucketLabel(bucket.bucketStart)), [buckets])
  const datasets = useMemo(
    () => [line(buckets, 'Launches', 'launches', COLOR_LAUNCHES), line(buckets, 'Completions', 'completions', COLOR_COMPLETIONS)],
    [buckets]
  )

  return (
    <section aria-labelledby="lms-launches-chart-title">
      <h3 className="h6" id="lms-launches-chart-title">Launches and completions</h3>
      <ChartBox label="Launches and completions over time">
        <ChartLine labels={labels} data={datasets} displayGrid displayTicks displayLegend legendPosition="bottom" />
      </ChartBox>
    </section>
  )
}

const OutcomeGroup = ({ testId, title, outcomes, totals }) =>
  <div className="mb-2" data-testid={testId}>
    <h4 className="fs-6 text-body-secondary mb-1">{title}</h4>
    <dl className="mb-0">
      {outcomes.map(outcome =>
        <div className="d-flex justify-content-between border-bottom py-1" key={outcome.key} data-testid={`outcome-${outcome.key}`}>
          <dt className="fw-normal">{outcome.label}</dt>
          <dd className="mb-0 fw-semibold">{formatCount(totals[outcome.key])}</dd>
        </div>
      )}
    </dl>
  </div>

// Sessions that completed are counted on the day they completed; launches
// that failed are counted on the day they were made. "Not passed" is a learner
// who finished below the pass mark; "Didn't start" is a launch that never
// began a session. The two groups keep them apart.
export const Outcomes = ({ totals }) =>
  <section aria-labelledby="lms-outcomes-title" data-testid="lms-outcomes">
    <h3 className="h6" id="lms-outcomes-title">Outcomes</h3>
    <OutcomeGroup testId="outcomes-completed" title="Sessions completed in the range" outcomes={COMPLETED_OUTCOMES} totals={totals} />
    <OutcomeGroup testId="outcomes-failed" title="Launches made in the range that failed" outcomes={FAILED_OUTCOMES} totals={totals} />
  </section>

export const Scores = ({ totals }) => {
  const scored = totals.scoredCompletions || 0
  const dataset = useMemo(
    () => ({ label: 'Scored completions', data: totals.scoreBuckets, backgroundColor: COLOR_SCORES, borderColor: COLOR_SCORES, borderWidth: 1 }),
    [totals.scoreBuckets]
  )

  return (
    <section className="mt-3" aria-labelledby="lms-scores-title" data-testid="lms-scores">
      <h3 className="h6" id="lms-scores-title">Scores</h3>
      <div className="d-flex flex-wrap gap-4 mb-2">
        <div data-testid="score-average">
          <span className="text-body-secondary">Average score</span>
          <span className="ms-2 fw-semibold">{formatScore(totals.scoreAverage)}</span>
        </div>
        <div data-testid="score-median">
          <span className="text-body-secondary">Median score</span>
          <span className="ms-2 fw-semibold">{formatScore(totals.scoreMedian)}</span>
        </div>
        <div data-testid="score-count" className="text-body-secondary">
          {scored} scored {scored === 1 ? 'completion' : 'completions'}
        </div>
      </div>
      {scored > 0
        ? <ChartBox label="Scored completions in 10-point buckets">
            <ChartBar labels={SCORE_LABELS} data={dataset} displayGrid displayTicks />
          </ChartBox>
        : <p className="text-body-secondary mb-0">No scored completions in this range.</p>}
    </section>
  )
}
